import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { TwcPopupRef, TwcPopupService } from '@topcon/twc-angular';

import {
    ACL,
    AclConfig,
    AclControlService,
    CategoryService,
    GroupService,
    LayerService,
    PermissionControlService,
    ProjectService,
    ToastService,
    TranslationService,
    UserService,
} from '@mirage-projects/cw-commons';
import {
    CatalogObject,
    CatalogObjectTypes,
    CatalogObjectTypesEnum,
    Group,
    PermAccessLevel,
    PermAccessLevelString,
    Permission,
    UserResponseData,
} from '@mirage-projects/cw-models';

import { AddGroupPopupComponent } from './add-group-popup/add-group-popup.component';
import { AddUserPopupComponent } from './add-user-popup/add-user-popup.component';
import { GroupMemberListComponent } from './group-member-list/group-member-list.component';
import { InheritedPermPopupComponent } from './inherited-perm-popup/inherited-perm-popup.component';
import { PermissionsEditorComponent } from './permissions-editor.component';
import { ActionPermissionList, PermissionListItem } from './permissions-list';

/**
 * This service implements the business logic for the Permission editor popup
 */
@Injectable({
    providedIn: 'root',
})
export class PermissionsEditorService {
    objectPermissions!: CatalogObject;
    objectOwner = '';
    userPermissionList: PermissionListItem[] = [];
    groupPermissionList: PermissionListItem[] = [];
    type!: CatalogObjectTypes;
    refPopup: TwcPopupRef | undefined;
    allUsers: UserResponseData[] = [];
    allGroups: Group[] = [];
    aclConfig: AclConfig[] = this.aclControlService.aclConfig;
    isLoadingGroupList = false;
    isLoadingUserList = false;
    selectedGroup: Group | undefined;
    selectedUser: UserResponseData | undefined;

    constructor(
        private popupService: TwcPopupService,
        private aclControlService: AclControlService,
        private categoryService: CategoryService,
        private layerService: LayerService,
        private projectService: ProjectService,
        private userService: UserService,
        private groupService: GroupService,
        private translationService: TranslationService,
        private permissionControlService: PermissionControlService,
        private toastService: ToastService
    ) {}

    /** Shows the Permission editor popup and initializes properties */
    editObjectPermissions(object: CatalogObject, type: CatalogObjectTypes): void {
        this.objectPermissions = object;
        this.type = type;
        this.refPopup = this.popupService.open(PermissionsEditorComponent);
        this.initialize();
    }

    /** Opens ViewGroupMember popup */
    openViewGroupMembersPopup(): void {
        this.popupService.open(GroupMemberListComponent);
    }

    /** Returns true if the View members group button should be disabled */
    isDisabledViewMembersGroupButton(): boolean {
        return !this.selectedGroup || !this.aclControlService.hasUserACL(ACL.GROUPS_GET_USERS);
    }

    /** Opens AddGroup popup */
    openAddGroupPopup(): void {
        this.popupService.open(AddGroupPopupComponent);
    }

    /** Returns true if the Add group button should be disabled */
    isDisabledAddGroupButton(): boolean {
        const hasUserACL = this.aclControlService.hasUserACL(ACL.GROUPS_ADD_OBJECT_PERMISSION);
        const canWriteByPermissions = this.permissionControlService.canWriteByPermissions(this.objectPermissions);
        return !hasUserACL || !canWriteByPermissions;
    }

    removeGroup(): void {
        if (!this.selectedGroup) {
            return;
        }
        this.isLoadingGroupList = true;

        this.groupService.deleteGroupPermission(this.selectedGroup.uid, this.idObject, this.type).subscribe({
            next: () => {
                this.loadGroups();
            },
            error: () => {
                this.toastService.error('PERMISSION_DIALOG.GROUP_PERMISSION_DELETE_FAILED');
                this.loadGroups();
            },
        });
    }

    /** Returns true if the Remove group button should be disabled */
    isDisabledRemoveGroupButton(): boolean {
        const hasUserACL = this.aclControlService.hasUserACL(ACL.GROUPS_REMOVE_OBJECT_PERMISSION);
        const canWriteByPermissions = this.permissionControlService.canWriteByPermissions(this.objectPermissions);
        return !hasUserACL || !canWriteByPermissions || !this.selectedGroup;
    }

    /** Opens AddGroup popup */
    openViewUserInheritsPopup(): void {
        this.popupService.open(InheritedPermPopupComponent);
    }

    /** Returns true if the View user inherits button should be disabled */
    isDisabledViewUserInherits(): boolean {
        return !this.aclControlService.hasUserACL(ACL.USERS_GET) || !this.selectedUser;
    }

    /** Opens AddUser popup */
    openAddUserPopup(): void {
        this.popupService.open(AddUserPopupComponent);
    }

    /** Returns true if the Add user button should be disabled */
    isDisabledAddUser(): boolean {
        const hasUserACL = this.aclControlService.hasUserACL(ACL.USER_ADD_OBJECT_PERMISSION);
        const canWriteByPermissions = this.permissionControlService.canWriteByPermissions(this.objectPermissions);
        return !hasUserACL || !canWriteByPermissions;
    }

    /** Removes the selected user from User Exceptions list */
    removeUser(): void {
        if (!this.selectedUser) {
            return;
        }
        this.isLoadingUserList = true;

        this.userService.deleteUserPermission(this.selectedUser.uid, this.idObject, this.type).subscribe({
            next: () => {
                this.loadUsers();
            },
            error: () => {
                this.toastService.error('PERMISSION_DIALOG.USER_PERMISSION_DELETE_FAILED');
                this.loadUsers();
            },
        });
    }

    /** Returns true if the Remove user button should be disabled */
    isDisabledRemoveUser(): boolean {
        const hasUserACL = this.aclControlService.hasUserACL(ACL.USERS_REMOVE_OBJECT_PERMISSION);
        const canWriteByPermissions = this.permissionControlService.canWriteByPermissions(this.objectPermissions);
        return !hasUserACL || !canWriteByPermissions || !this.selectedUser;
    }

    onSelectItemUser(idUser: string): void {
        this.selectedUser = this.allUsers.find((u) => u.uid === idUser);
    }

    onActionUserClick(action: ActionPermissionList): void {
        this.isLoadingUserList = true;
        let effect: PermAccessLevelString;
        switch (action.iconAction.id) {
            case 'view': {
                effect = action.iconAction.status === 'active' ? PermAccessLevel.NONE : PermAccessLevel.READONLY;
                break;
            }
            case 'edit': {
                effect = action.iconAction.status === 'active' ? PermAccessLevel.READONLY : PermAccessLevel.WRITE;
                break;
            }
            case 'delete': {
                effect = action.iconAction.status === 'active' ? PermAccessLevel.WRITE : PermAccessLevel.FULL;
                break;
            }
            default:
                effect = action.iconAction.status === 'active' ? PermAccessLevel.NONE : PermAccessLevel.READONLY;
                break;
        }
        this.addUserPermission(action.idItem, effect).subscribe(() => this.loadUsers());
    }

    onSelectItemGroup(idGroup: string): void {
        this.selectedGroup = this.allGroups.find((g) => g.uid === idGroup);
    }

    onActionGroupClick(action: ActionPermissionList): void {
        switch (action.iconAction.id) {
            case 'view':
                this.toastService.generic('PERMISSION_DIALOG.NO_VIEW_CHANGE_IN_GROUPS');
                break;

            case 'edit': {
                this.isLoadingGroupList = true;
                const effect = action.iconAction.status === 'active' ? PermAccessLevel.READONLY : PermAccessLevel.WRITE;
                this.addGroupPermission(action.idItem, effect).subscribe(() => this.loadGroups());
                break;
            }
            case 'delete': {
                this.isLoadingGroupList = true;
                const effect = action.iconAction.status === 'active' ? PermAccessLevel.WRITE : PermAccessLevel.FULL;
                this.addGroupPermission(action.idItem, effect).subscribe(() => this.loadGroups());
                break;
            }
            default:
                break;
        }
    }

    getFullName(userData: UserResponseData): string {
        if (!userData.userFirstName) {
            return userData.userName;
        }
        return this.translationService.translate('LOCALE.ORDER_NAME', {
            firstName: userData.userFirstName,
            lastName: userData.userLastName,
        });
    }

    addUserPermission(userId: string, level: PermAccessLevelString): Observable<void> {
        return this.userService.addUserPermission(userId, this.idObject, this.type, level);
    }

    addGroupPermission(groupUid: string, level: PermAccessLevelString): Observable<void> {
        return this.groupService.addGroupPermission(groupUid, this.idObject, this.type, level);
    }

    loadUsers(): void {
        this.isLoadingUserList = true;
        this.allUsers = [];
        this.userPermissionList = [];
        this.userService.getAll().subscribe((usersData) => {
            this.allUsers = usersData;
            this.allUsers.forEach((usersData) => {
                usersData.permissions?.forEach((perm) => {
                    if (perm.type === this.type && perm.id === this.idObject) {
                        const item = this.getPermListItemForUser(usersData, perm);
                        this.userPermissionList.push(item);
                    }
                });
            });
            this.selectedUser = undefined;
            this.isLoadingUserList = false;
        });
    }

    loadGroups(): void {
        this.isLoadingGroupList = true;
        this.allGroups = [];
        this.groupPermissionList = [];
        this.groupService.getAll().subscribe((allGroups) => {
            this.allGroups = allGroups;
            this.allGroups.forEach((group) => {
                group.permissions?.forEach((perm) => {
                    if (perm.type === this.type && perm.id === this.idObject) {
                        const item = this.getPermListItemForGroup(group, perm);
                        this.groupPermissionList.push(item);
                    }
                });
            });
            this.selectedGroup = undefined;
            this.isLoadingGroupList = false;
        });
    }

    private initialize(): void {
        this.allGroups = [];
        this.groupPermissionList = [];
        this.allUsers = [];
        this.userPermissionList = [];
        this.objectOwner = '';
        this.getObject()?.subscribe((obj) => {
            const objectOwnerId: string = obj.creatorUserId;
            if (objectOwnerId) {
                this.userService.get(objectOwnerId).subscribe((userData) => {
                    this.objectOwner = this.getFullName(userData);
                });
            }
        });
        this.loadUsers();
        this.loadGroups();
    }

    private getObject(): Observable<CatalogObject> | undefined {
        switch (this.type) {
            case CatalogObjectTypesEnum.projects:
                return this.projectService.get(this.idObject);
            case CatalogObjectTypesEnum.categories:
                return this.categoryService.getCategory(this.idObject as number);
            case CatalogObjectTypesEnum.layers:
                return this.layerService.get(this.idObject as number);
            case CatalogObjectTypesEnum.jobSites:
                // TODO: Support job sites
                break;
            default:
                break;
        }
        return undefined;
    }

    private getPermListItemForGroup(group: Group, perm: Permission): PermissionListItem {
        const canEditObject = this.permissionControlService.canWriteByPermissions(this.objectPermissions);
        const canAddPermission = canEditObject && this.aclControlService.hasUserACL(ACL.GROUPS_ADD_OBJECT_PERMISSION);
        const canRemovePermission = canEditObject && this.aclControlService.hasUserACL(ACL.GROUPS_REMOVE_OBJECT_PERMISSION);
        return {
            id: group.uid,
            name: group.name,
            iconsAction: [
                {
                    id: 'view',
                    icon: 'eye-show',
                    status: this.hasViewPerm(perm) ? 'active' : 'inactive',
                    isDisabled: this.hasViewPerm(perm) ? !canRemovePermission : !canAddPermission,
                    tooltip: this.hasViewPerm(perm) ? 'PERMISSION_DIALOG.REMOVE_VIEW_PERMISSION' : 'PERMISSION_DIALOG.ADD_VIEW_PERMISSION',
                },
                {
                    id: 'edit',
                    icon: 'draw',
                    status: this.hasUpdatePermission(perm) ? 'active' : 'inactive',
                    isDisabled: this.hasUpdatePermission(perm) ? !canRemovePermission : !canAddPermission,
                    tooltip: this.hasUpdatePermission(perm)
                        ? 'PERMISSION_DIALOG.REMOVE_EDIT_PERMISSION'
                        : 'PERMISSION_DIALOG.ADD_EDIT_PERMISSION',
                },
                {
                    id: 'delete',
                    icon: 'delete',
                    status: this.hasDeletePermission(perm) ? 'active' : 'inactive',
                    isDisabled: this.hasDeletePermission(perm) ? !canRemovePermission : !canAddPermission,
                    tooltip: this.hasDeletePermission(perm)
                        ? 'PERMISSION_DIALOG.REMOVE_DELETE_PERMISSION'
                        : 'PERMISSION_DIALOG.ADD_DELETE_PERMISSION',
                },
            ],
        };
    }

    private getPermListItemForUser(userData: UserResponseData, perm: Permission): PermissionListItem {
        const canEditObject = this.permissionControlService.canWriteByPermissions(this.objectPermissions);
        const canAddPermission = canEditObject && this.aclControlService.hasUserACL(ACL.USER_ADD_OBJECT_PERMISSION);
        const canRemovePermission = canEditObject && this.aclControlService.hasUserACL(ACL.USERS_REMOVE_OBJECT_PERMISSION);
        return {
            id: userData.uid,
            name: this.getFullName(userData),
            iconsAction: [
                {
                    id: 'view',
                    icon: 'eye-show',
                    status: this.hasViewPerm(perm) ? 'active' : 'inactive',
                    isDisabled: this.hasViewPerm(perm) ? !canRemovePermission : !canAddPermission,
                    tooltip: this.hasViewPerm(perm) ? 'PERMISSION_DIALOG.REMOVE_VIEW_PERMISSION' : 'PERMISSION_DIALOG.ADD_VIEW_PERMISSION',
                },
                {
                    id: 'edit',
                    icon: 'draw',
                    status: this.hasUpdatePermission(perm) ? 'active' : 'inactive',
                    isDisabled: this.hasUpdatePermission(perm) ? !canRemovePermission : !canAddPermission,
                    tooltip: this.hasUpdatePermission(perm)
                        ? 'PERMISSION_DIALOG.REMOVE_EDIT_PERMISSION'
                        : 'PERMISSION_DIALOG.ADD_EDIT_PERMISSION',
                },
                {
                    id: 'delete',
                    icon: 'delete',
                    status: this.hasDeletePermission(perm) ? 'active' : 'inactive',
                    isDisabled: this.hasDeletePermission(perm) ? !canRemovePermission : !canAddPermission,
                    tooltip: this.hasDeletePermission(perm)
                        ? 'PERMISSION_DIALOG.REMOVE_DELETE_PERMISSION'
                        : 'PERMISSION_DIALOG.ADD_DELETE_PERMISSION',
                },
            ],
        };
    }

    private hasViewPerm(perm: Permission): boolean {
        const hasViewPerm = perm.effect !== PermAccessLevel.NONE;
        return hasViewPerm;
    }

    private hasUpdatePermission(perm: Permission): boolean {
        const hasUpdatePermission = perm.effect === PermAccessLevel.WRITE || perm.effect === PermAccessLevel.FULL;
        return hasUpdatePermission;
    }

    private hasDeletePermission(perm: Permission): boolean {
        const hasDeletePermission = perm.effect === PermAccessLevel.FULL;
        return hasDeletePermission;
    }

    get permissionsObjectName(): string {
        return this.objectPermissions.getName?.() ?? this.objectPermissions.name;
    }

    get idObject(): number {
        return this.objectPermissions.getId?.() ?? this.objectPermissions.id;
    }
}
